import {StateCreator} from "zustand/vanilla"
import logger from "@/app/logger"
import {repoCommentsAPI, repoCommentAPI} from "@/app/apiPaths"
import {RepoResearchComment} from "@/pb/api/devplan/types/comments"
import {InlineComment, InlineCommentSelection} from "@/pb/api/devplan/types/inline_comments"
import {
  GetRepoResearchCommentsResponse,
  CreateRepoResearchCommentRequest,
  CreateRepoResearchCommentResponse,
  UpdateRepoResearchCommentRequest,
  UpdateRepoResearchCommentResponse,
} from "@/pb/api/devplan/services/web/company"

/**
 * Represents an inline comment attached to a text anchor in a Knowledge area.
 * All fields support UI display and cross-component access.
 */
export interface InlineCommentData {
  id: string
  repoId: string
  knowledgeArea: string // "summary" | "build_process" | "tech_stack" | "user_flows" etc.
  startOffset: number
  endOffset: number
  anchorText: string
  author: string
  createdAt: Date
  updatedAt: Date
  content: string
}

/**
 * Convert RepoResearchComment proto to InlineCommentData for UI consumption.
 */
function toInlineCommentData(proto: RepoResearchComment): InlineCommentData {
  const selection = proto.comment?.selection
  return {
    id: proto.id,
    repoId: proto.repoId,
    knowledgeArea: proto.area ?? '',
    startOffset: selection?.startOffset ?? 0,
    endOffset: selection?.endOffset ?? 0,
    anchorText: selection?.anchorText ?? '',
    author: proto.meta?.createdBy ?? proto.comment?.authorId ?? '',
    createdAt: proto.meta?.createdAt ?? new Date(),
    updatedAt: proto.meta?.updatedAt ?? new Date(),
    content: proto.comment?.content ?? '',
  }
}

/**
 * Convert InlineCommentData to RepoResearchComment proto for API calls.
 */
function toRepoResearchComment(data: InlineCommentData): RepoResearchComment {
  return RepoResearchComment.create({
    id: data.id,
    repoId: data.repoId,
    area: data.knowledgeArea,
    comment: InlineComment.create({
      id: data.id,
      content: data.content,
      authorId: data.author,
      selection: InlineCommentSelection.create({
        startOffset: data.startOffset,
        endOffset: data.endOffset,
        anchorText: data.anchorText,
      }),
    }),
  })
}

/**
 * Zustand slice state for inline comments.
 * Repository-scoped: all data is keyed by repoId.
 */
export type InlineCommentsStoreState = {
  // Comments indexed by repoId -> commentId -> InlineCommentData
  inlineComments: Record<string, Record<string, InlineCommentData>>

  // Regeneration state by repoId
  regeneratingRepos: Record<string, boolean>

  // Cached counts by repoId -> research area key (e.g. "build_process") -> count
  // Only updated via explicit refreshCounts call or fetchCommentsForRepo
  cachedCounts: Record<string, Record<string, number>>

  // CRUD operations - backed by database via API
  addInlineComment: (companyId: number, comment: InlineCommentData) => Promise<InlineCommentData>
  updateInlineComment: (companyId: number, repoId: string, commentId: string, updates: Partial<InlineCommentData>) => Promise<InlineCommentData>
  deleteInlineComment: (companyId: number, repoId: string, commentId: string) => Promise<void>

  // Getters - read from local store
  getCommentsForRepo: (repoId: string) => InlineCommentData[]
  getCommentsByKnowledgeArea: (repoId: string, knowledgeArea: string) => InlineCommentData[]
  getCommentCountsByArea: (repoId: string) => Record<string, number>

  // Regeneration state
  setRegenerating: (repoId: string, isRegenerating: boolean) => void
  isRegenerating: (repoId: string) => boolean

  // Tab counts refresh - computes actual counts from current comments
  refreshCounts: (repoId: string) => void

  // Fetch comments from DB store and populate local store
  fetchCommentsForRepo: (companyId: number, repoId: string) => Promise<void>
  // Clear comments from local store (server-side deletion is handled by the guidance route)
  clearCommentsForRepo: (repoId: string) => void
}

/**
 * Create the inline comments Zustand slice.
 * Follows the same pattern as commentsStore.tsx.
 */
export const createInlineCommentsSlice: StateCreator<
  InlineCommentsStoreState,
  [],
  [],
  InlineCommentsStoreState
> = (set, get) => ({
  inlineComments: {},
  regeneratingRepos: {},
  cachedCounts: {},

  addInlineComment: async (companyId, comment) => {
    const proto = toRepoResearchComment(comment)

    const response = await fetch(repoCommentsAPI(companyId, comment.repoId), {
      method: 'POST',
      body: JSON.stringify(CreateRepoResearchCommentRequest.toJSON({comment: proto}))
    })

    if (!response.ok) {
      throw new Error(response.statusText)
    }

    const json = await response.json()
    const resp = CreateRepoResearchCommentResponse.fromJSON(json)
    const created = toInlineCommentData(resp.comment!)

    set(s => {
      const repoComments = s.inlineComments[comment.repoId] ?? {}
      return {
        ...s,
        inlineComments: {
          ...s.inlineComments,
          [comment.repoId]: {
            ...repoComments,
            [created.id]: created,
          },
        },
      }
    })

    logger.debug({repoId: comment.repoId, commentId: created.id}, "Inline comment added")
    return created
  },

  updateInlineComment: async (companyId, repoId, commentId, updates) => {
    const existing = get().inlineComments[repoId]?.[commentId]
    if (!existing) {
      throw new Error("Comment not found")
    }

    const updated: InlineCommentData = {...existing, ...updates, id: commentId, repoId}
    const proto = toRepoResearchComment(updated)

    const response = await fetch(repoCommentAPI(companyId, repoId, commentId), {
      method: 'PATCH',
      body: JSON.stringify(UpdateRepoResearchCommentRequest.toJSON({comment: proto}))
    })

    if (!response.ok) {
      throw new Error(response.statusText)
    }

    const json = await response.json()
    const resp = UpdateRepoResearchCommentResponse.fromJSON(json)
    const result = toInlineCommentData(resp.comment!)

    set(s => {
      const repoComments = s.inlineComments[repoId] ?? {}
      return {
        ...s,
        inlineComments: {
          ...s.inlineComments,
          [repoId]: {
            ...repoComments,
            [commentId]: result,
          },
        },
      }
    })

    logger.debug({repoId, commentId}, "Inline comment updated")
    return result
  },

  deleteInlineComment: async (companyId, repoId, commentId) => {
    const response = await fetch(repoCommentAPI(companyId, repoId, commentId), {
      method: 'DELETE',
    })

    if (!response.ok) {
      throw new Error(response.statusText)
    }

    set(s => {
      const repoComments = s.inlineComments[repoId]
      if (!repoComments) {
        return s
      }
      const {[commentId]: _, ...remaining} = repoComments
      return {
        ...s,
        inlineComments: {
          ...s.inlineComments,
          [repoId]: remaining,
        },
      }
    })

    logger.debug({repoId, commentId}, "Inline comment deleted")
  },

  getCommentsForRepo: (repoId) => {
    const repoComments = get().inlineComments[repoId] ?? {}
    return Object.values(repoComments)
  },

  getCommentsByKnowledgeArea: (repoId, knowledgeArea) => {
    const repoComments = get().inlineComments[repoId] ?? {}
    return Object.values(repoComments).filter(c => c.knowledgeArea === knowledgeArea)
  },

  getCommentCountsByArea: (repoId) => {
    // Returns cached counts - use refreshCounts to update from actual data
    return get().cachedCounts[repoId] ?? {}
  },

  setRegenerating: (repoId, isRegenerating) => {
    set(s => ({
      ...s,
      regeneratingRepos: {
        ...s.regeneratingRepos,
        [repoId]: isRegenerating,
      },
    }))
    logger.debug({repoId, isRegenerating}, isRegenerating ? "Regeneration started" : "Regeneration completed")
  },

  isRegenerating: (repoId) => {
    return get().regeneratingRepos[repoId] ?? false
  },

  clearCommentsForRepo: (repoId) => {
    set(s => {
      const {[repoId]: _, ...remainingComments} = s.inlineComments
      const {[repoId]: __, ...remainingCounts} = s.cachedCounts
      return {
        ...s,
        inlineComments: remainingComments,
        cachedCounts: remainingCounts,
      }
    })
    logger.debug({repoId}, "Local comments cache cleared")
  },

  refreshCounts: (repoId) => {
    // Defer state update to next microtask to avoid "setState during render" errors
    // This can happen when effects from sibling components run during another component's render
    queueMicrotask(() => {
      const repoComments = get().inlineComments[repoId] ?? {}
      const counts: Record<string, number> = {}

      // Count comments by knowledge area
      Object.values(repoComments).forEach(comment => {
        counts[comment.knowledgeArea] = (counts[comment.knowledgeArea] ?? 0) + 1
      })

      set(s => ({
        ...s,
        cachedCounts: {
          ...s.cachedCounts,
          [repoId]: counts,
        },
      }))
      logger.debug({repoId, counts}, "Comment counts refreshed")
    })
  },

  fetchCommentsForRepo: async (companyId, repoId) => {
    const response = await fetch(repoCommentsAPI(companyId, repoId))

    if (!response.ok) {
      throw new Error(response.statusText)
    }

    const json = await response.json()
    const resp = GetRepoResearchCommentsResponse.fromJSON(json)

    // Convert proto comments to InlineCommentData and index by id
    const comments: Record<string, InlineCommentData> = {}
    for (const proto of resp.comments) {
      const data = toInlineCommentData(proto)
      comments[data.id] = data
    }

    // Extract grouped counts from response
    const counts = resp.groupedCounts?.countsByArea ?? {}

    set(s => ({
      ...s,
      inlineComments: {
        ...s.inlineComments,
        [repoId]: comments,
      },
      cachedCounts: {
        ...s.cachedCounts,
        [repoId]: counts,
      },
    }))

    logger.debug({repoId, count: resp.comments.length}, "Fetched inline comments from API")
  },
})
